<script lang="ts">
  import type { Driver } from '../types';
  import { dispatcherApi } from '../utils/api';
  import DeliveryCard from './DeliveryCard.svelte';

  interface Props {
    driver_id: string;
    driver: Driver;
    isSelected?: boolean;
    onSelect?: (driver_id: string) => void;
    onRouteView?: (driver_id: string, route: any[]) => void;
    /**
     * Nonce que App.svelte incrementa cuando llega un `delivery_status_changed`
     * por WebSocket (driver marcó una entrega desde la app móvil). Cuando
     * cambia y este DriverCard está abierto, re-consultamos `/deliveries`
     * para que el listado y los contadores reflejen el nuevo estado sin
     * tener que colapsar/expandir la tarjeta a mano.
     */
    refreshNonce?: number;
  }

  let { driver_id, driver, isSelected = false, onSelect, onRouteView, refreshNonce = 0 }: Props = $props();
  let isExpanded = $state(false);
  let deliveries = $state<any>({ pending: [], completed: [], failed: [] });
  let loadingDeliveries = $state(false);
  let activeTab = $state<'pending' | 'completed' | 'failed'>('pending');
  let displayCount = $state(10);

  // Cuando cambia el nonce por un evento WebSocket, forzamos refetch si la
  // tarjeta está abierta. Si está cerrada nos ahorramos la llamada — el
  // fetch va a ocurrir cuando el operador la abra de nuevo.
  $effect(() => {
    // Referencia al nonce para que Svelte lo considere dependencia.
    const _ = refreshNonce;
    if (isExpanded && refreshNonce > 0) {
      loadDeliveries();
    }
  });
  
  let visibleDeliveries = $derived.by(() => {
    return deliveries[activeTab]?.slice(0, displayCount) || [];
  });
  
  let hasMore = $derived.by(() => {
    return (deliveries[activeTab]?.length || 0) > displayCount;
  });

  let statusClass = $derived.by(() => {
    const lastUpdate = driver.last_update ? new Date(driver.last_update) : null;
    const isActive = lastUpdate && Date.now() - lastUpdate.getTime() < 60000;
    
    if (driver.connected) {
      return isActive ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400';
    }
    return 'bg-blue-500/20 text-blue-400';
  });

  let statusText = $derived.by(() => {
    const lastUpdate = driver.last_update ? new Date(driver.last_update) : null;
    const isActive = lastUpdate && Date.now() - lastUpdate.getTime() < 60000;
    
    if (driver.connected) {
      return isActive ? '🟢 En línea' : '⏱️ Conectado';
    }
    return '📍 Disponible';
  });

  async function handleClick() {
    isExpanded = !isExpanded;
    if (onSelect) {
      onSelect(driver_id);
    }

    if (isExpanded) {
      if (deliveries.pending.length === 0) {
        await loadDeliveries();
      } else {
        // If deliveries already loaded, show current tab on map
        updateMapWithCurrentTab();
      }
    }
  }

  function updateMapWithCurrentTab() {
    if (onRouteView) {
      const currentTabDeliveries = deliveries[activeTab] || [];
      console.log(`📍 Showing ${activeTab} deliveries on map: ${currentTabDeliveries.length} items`);
      onRouteView(driver_id, currentTabDeliveries);
    }
  }

  function handleTabChange(tab: 'pending' | 'completed' | 'failed') {
    activeTab = tab;
    displayCount = 10;
    // Update map with new tab's deliveries
    updateMapWithCurrentTab();
  }

  function handleDeliveryClick(delivery: any) {
    // Show only this delivery on map
    if (onRouteView) {
      console.log(`📍 Showing single delivery on map: ${delivery.id}`);
      onRouteView(driver_id, [delivery]);
    }
  }

  /**
   * Callback que dispara DeliveryCard cuando el dispatcher guarda un
   * cambio de estado/notas. Antes DriverCard no lo pasaba: el backend
   * grababa bien en driver_ots pero la tarjeta seguía mostrando el
   * estado viejo hasta cerrar/abrir la tarjeta. Ahora movemos la
   * entrega al bucket correcto (pending/completed/failed) en el
   * cliente, para feedback inmediato.
   */
  function handleDeliveryUpdate(deliveryId: string, updates: { status?: string; notes?: string }) {
    const isCompleted = (s: string | undefined) => !!s && ['ENTREGADO', 'EN DESCARGO', 'EN RECEPCION'].includes(s);
    const isFailed    = (s: string | undefined) => !!s && ['EN SOBRANCIA', 'EN RETENCION'].includes(s);
    const bucketFor   = (s: string | undefined): 'pending' | 'completed' | 'failed' =>
      isCompleted(s) ? 'completed' : isFailed(s) ? 'failed' : 'pending';

    // Buscar la entrega en cualquiera de los 3 buckets.
    const buckets: Array<'pending' | 'completed' | 'failed'> = ['pending', 'completed', 'failed'];
    let found: any = null;
    let fromBucket: 'pending' | 'completed' | 'failed' | null = null;
    for (const b of buckets) {
      const idx = (deliveries[b] || []).findIndex((d: any) => String(d.id) === String(deliveryId));
      if (idx >= 0) {
        found = deliveries[b][idx];
        fromBucket = b;
        // Sacar de la lista actual (asignación nueva para triggear reactivity de Svelte 5)
        deliveries[b] = deliveries[b].filter((_: any, i: number) => i !== idx);
        break;
      }
    }
    if (!found || !fromBucket) return;

    const patched = { ...found, ...updates };
    const target = bucketFor(patched.status);
    deliveries[target] = [patched, ...(deliveries[target] || [])];
    // Reasignar el objeto raíz para asegurar reactividad de los $derived.
    deliveries = { ...deliveries };
  }

  async function loadDeliveries() {
    if (loadingDeliveries) return;
    
    loadingDeliveries = true;
    try {
      console.log(`📍 Loading deliveries for driver ${driver_id}`);
      const response = await dispatcherApi.getDriverDeliveries(driver_id);
      console.log(`📍 Response:`, response);
      if (response.success) {
        const data = response.data as any;
        deliveries = {
          pending: data.pending || [],
          completed: data.completed || [],
          failed: data.failed || []
        };
        
        console.log(`📍 Loaded ${deliveries.pending.length} pending deliveries`);
        
        // Show ALL deliveries on map when expanded (pending, completed, failed)
        if (onRouteView) {
          const allDeliveries = [...deliveries.pending, ...deliveries.completed, ...deliveries.failed];
          console.log(`📍 Calling onRouteView with ${allDeliveries.length} total deliveries`);
          onRouteView(driver_id, allDeliveries);
        }
      }
    } catch (error) {
      console.error('Error loading deliveries:', error);
      deliveries = { pending: [], completed: [], failed: [] };
    } finally {
      loadingDeliveries = false;
    }
  }

  let cardClass = $derived(
    `bg-white/10 border transition-all cursor-pointer rounded-lg p-2 mb-2 text-xs ${
      isSelected
        ? 'border-[#146efb] bg-[#146efb]/15 shadow-lg shadow-[#146efb]/40 driver-card active'
        : 'border-white/20 hover:border-white/40 driver-card'
    }`
  );
</script>

<div class={cardClass}>
  <button
    type="button"
    class="w-full flex justify-between items-start p-2 rounded text-left"
    onclick={handleClick}
    aria-expanded={isExpanded}
  >
    <div class="flex-1">
      <div class="font-bold text-white">{driver.name || 'Conductor'}</div>
      <div class="text-gray-400">Entregas: {driver.deliveries_count || 0}</div>
      <span class="{statusClass} px-2 py-1 rounded inline-block mt-1 text-[10px]">{statusText}</span>
    </div>
    <div class="text-lg text-gray-400 transition-transform" class:rotate-180={isExpanded}>▼</div>
  </button>

  {#if isExpanded}
    <div class="border-t border-white/10 mt-2 pt-2 px-2">
      <!-- Delivery Summary - Clickable Tabs -->
      {#if !loadingDeliveries}
        <div class="grid grid-cols-3 gap-2 mb-3 text-center text-xs">
          <button
            onclick={() => handleTabChange('pending')}
            class="rounded p-2 transition {activeTab === 'pending' ? 'bg-yellow-500/40 border-2 border-yellow-400' : 'bg-yellow-500/20 border-2 border-transparent hover:bg-yellow-500/30'}"
          >
            <div class="text-gray-400">Pendientes</div>
            <div class="font-bold text-yellow-400">{deliveries.pending.length}</div>
          </button>
          <button
            onclick={() => handleTabChange('completed')}
            class="rounded p-2 transition {activeTab === 'completed' ? 'bg-green-500/40 border-2 border-green-400' : 'bg-green-500/20 border-2 border-transparent hover:bg-green-500/30'}"
          >
            <div class="text-gray-400">Completadas</div>
            <div class="font-bold text-green-400">{deliveries.completed.length}</div>
          </button>
          <button
            onclick={() => handleTabChange('failed')}
            class="rounded p-2 transition {activeTab === 'failed' ? 'bg-red-500/40 border-2 border-red-400' : 'bg-red-500/20 border-2 border-transparent hover:bg-red-500/30'}"
          >
            <div class="text-gray-400">Fallidas</div>
            <div class="font-bold text-red-400">{deliveries.failed.length}</div>
          </button>
        </div>
      {:else}
        <div class="text-center text-gray-500 text-xs py-2">Cargando entregas...</div>
      {/if}

      <!-- Delivery Details -->
      {#if !loadingDeliveries && visibleDeliveries.length > 0}
        <div class="mt-3 max-h-60 overflow-y-auto">
          {#each visibleDeliveries as delivery}
            <DeliveryCard
              {delivery}
              {driver_id}
              onDeliveryClick={handleDeliveryClick}
              onDeliveryUpdate={handleDeliveryUpdate}
            />
          {/each}
          
          {#if hasMore}
            <button
              onclick={() => displayCount += 10}
              class="w-full mt-2 py-2 text-center text-gray-400 hover:text-gray-300 text-xs font-semibold transition"
            >
              Cargar más ({displayCount} de {deliveries[activeTab].length})
            </button>
          {/if}
        </div>
      {:else if !loadingDeliveries}
        <div class="text-center text-gray-500 text-xs py-4">
          Sin {activeTab === 'pending' ? 'pendientes' : activeTab === 'completed' ? 'completadas' : 'fallidas'}
        </div>
      {/if}
    </div>
  {/if}
</div>
